import React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Box, Button, Flex, LoadingOrbit, TagBadge, Text, TextField, useToast } from '@chatsdet/ui';
import { ChatCircleText, Plus, Trash } from '@phosphor-icons/react';
import { API_URL } from '../../../config';
import { toApiError } from '../../../lib/api-error';
import { useAuth } from '../../../contexts/AuthContext';

type TextMessage = {
  id: string;
  direction: 'inbound' | 'outbound';
  from: string;
  to: string;
  body: string;
  status: string;
  segmentCount: number;
  sentAt: string | null;
  receivedAt: string | null;
  createdAt: string;
};

type Destination = { id: string; phoneNumber: string; label: string | null };

const fetchJson = async <T,>(url: string, init?: RequestInit): Promise<T> => {
  const response = await fetch(url, { credentials: 'include', ...init });
  if (!response.ok) throw await toApiError(response);
  return response.json() as Promise<T>;
};

export function PropertyTextMessages({ propertyId }: { propertyId: string }) {
  const { user } = useAuth();
  const canManageDestinations = user?.role === 'admin';
  const [selectedId, setSelectedId] = React.useState<string | null>(null);
  const [search, setSearch] = React.useState('');
  const query = useQuery({
    queryKey: ['text-messages', propertyId, search],
    queryFn: () =>
      fetchJson<{ items: TextMessage[]; totalItems: number }>(
        `${API_URL}/messaging/messages?propertyId=${encodeURIComponent(propertyId)}&search=${encodeURIComponent(search)}&pageSize=50`
      ),
    refetchInterval: 5_000,
  });
  const messages = query.data?.items ?? [];
  const selected = messages.find((message) => message.id === selectedId) ?? messages[0] ?? null;

  React.useEffect(() => {
    if (selected && selected.id !== selectedId) setSelectedId(selected.id);
  }, [selected, selectedId]);

  if (query.isLoading) {
    return (
      <Flex justify="center" className="py-16">
        <LoadingOrbit size="lg" />
      </Flex>
    );
  }

  return (
    <Flex direction="column" gap="md">
      <Flex align="end" justify="between" gap="md" wrap>
        <Box className="w-[320px]">
          <TextField
            label="Search texts"
            placeholder="Message body…"
            value={search}
            onChange={setSearch}
          />
        </Box>
        <Flex align="center" gap="sm">
          <TagBadge name={`${query.data?.totalItems ?? 0} messages`} color="gray" hideDot />
          {canManageDestinations && <DestinationAllowlist />}
        </Flex>
      </Flex>

      <Flex gap="lg" className="min-h-[520px]">
        <Box className="w-[380px] border border-border/60 rounded-lg overflow-y-auto bg-card/40">
          {messages.length === 0 ? (
            <Flex
              direction="column"
              align="center"
              justify="center"
              className="h-full p-8 text-center"
              gap="sm"
            >
              <ChatCircleText size={32} className="text-muted-foreground" />
              <Text size="sm" className="text-muted-foreground">
                No text messages yet.
              </Text>
              <Text size="xs" className="text-muted-foreground">
                Provision a phone number on a credential, then use it during a test or receive a
                verification text.
              </Text>
            </Flex>
          ) : (
            messages.map((message) => (
              <button
                type="button"
                key={message.id}
                onClick={() => setSelectedId(message.id)}
                className={`block w-full border-b border-border/50 p-3 text-left hover:bg-muted/40 ${selected?.id === message.id ? 'bg-muted/60' : ''}`}
              >
                <Flex align="center" justify="between" gap="sm">
                  <Text size="sm" weight="medium" className="truncate">
                    {message.direction === 'inbound' ? message.from : message.to}
                  </Text>
                  <TagBadge
                    name={message.direction === 'inbound' ? 'Received' : message.status}
                    color={
                      message.direction === 'inbound'
                        ? 'blue'
                        : message.status === 'delivered'
                          ? 'green'
                          : 'gray'
                    }
                    hideDot
                    size="sm"
                  />
                </Flex>
                <Text size="sm" className="mt-1 line-clamp-2 text-muted-foreground">
                  {message.body}
                </Text>
                <Text size="xs" className="mt-2 text-muted-foreground">
                  {new Date(
                    message.receivedAt ?? message.sentAt ?? message.createdAt
                  ).toLocaleString()}
                </Text>
              </button>
            ))
          )}
        </Box>

        <Box className="flex-1 border border-border/60 rounded-lg bg-card/40 p-6">
          {selected ? (
            <Flex direction="column" gap="md">
              <Flex align="center" justify="between">
                <Text weight="semibold">
                  {selected.direction === 'inbound' ? `From ${selected.from}` : `To ${selected.to}`}
                </Text>
                <TagBadge
                  name={`${selected.segmentCount} segment${selected.segmentCount === 1 ? '' : 's'}`}
                  color="gray"
                  hideDot
                />
              </Flex>
              <Text size="sm" className="text-muted-foreground">
                {selected.from} → {selected.to}
              </Text>
              <Box className="rounded-lg border border-border bg-background p-4 whitespace-pre-wrap">
                <Text>{selected.body}</Text>
              </Box>
            </Flex>
          ) : (
            <Flex align="center" justify="center" className="h-full">
              <Text size="sm" className="text-muted-foreground">
                Select a text message.
              </Text>
            </Flex>
          )}
        </Box>
      </Flex>
    </Flex>
  );
}

function DestinationAllowlist() {
  const [open, setOpen] = React.useState(false);
  const [phoneNumber, setPhoneNumber] = React.useState('');
  const [label, setLabel] = React.useState('');
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const query = useQuery({
    queryKey: ['sms-destinations'],
    queryFn: () => fetchJson<{ destinations: Destination[] }>(`${API_URL}/messaging/destinations`),
    enabled: open,
  });
  const add = useMutation({
    mutationFn: () =>
      fetchJson(`${API_URL}/messaging/destinations`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ phoneNumber, label: label || undefined }),
      }),
    onSuccess: () => {
      setPhoneNumber('');
      setLabel('');
      queryClient.invalidateQueries({ queryKey: ['sms-destinations'] });
      toast.success('SMS destination allowed');
    },
    onError: (error) =>
      toast.error(error instanceof Error ? error.message : 'Failed to add destination'),
  });
  const remove = useMutation({
    mutationFn: (id: string) =>
      fetchJson(`${API_URL}/messaging/destinations/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['sms-destinations'] }),
  });

  return (
    <Box className="relative">
      <Button variant="outline" size="sm" onPress={() => setOpen((value) => !value)}>
        Allowed destinations
      </Button>
      {open && (
        <Box className="absolute right-0 z-20 mt-2 w-[420px] rounded-lg border border-border bg-card p-4 shadow-lg">
          <Text weight="semibold">Allowed SMS destinations</Text>
          <Text size="xs" className="mt-1 text-muted-foreground">
            Agents may also reply for 24 hours to numbers that texted their assigned number.
          </Text>
          <Flex gap="sm" className="mt-3">
            <Box className="flex-1">
              <TextField
                aria-label="Phone number"
                placeholder="+14155550123"
                value={phoneNumber}
                onChange={setPhoneNumber}
              />
            </Box>
            <Box className="flex-1">
              <TextField
                aria-label="Destination label"
                placeholder="QA handset"
                value={label}
                onChange={setLabel}
              />
            </Box>
            <Button
              variant="primary"
              size="sm"
              iconOnly
              onPress={() => add.mutate()}
              isDisabled={!phoneNumber || add.isPending}
            >
              <Plus size={16} />
            </Button>
          </Flex>
          <Flex direction="column" gap="xs" className="mt-3">
            {(query.data?.destinations ?? []).map((destination) => (
              <Flex
                key={destination.id}
                align="center"
                justify="between"
                className="rounded bg-muted/30 px-2 py-1"
              >
                <Text size="sm">
                  {destination.label ? `${destination.label} · ` : ''}
                  {destination.phoneNumber}
                </Text>
                <Button
                  variant="ghost"
                  size="sm"
                  iconOnly
                  onPress={() => remove.mutate(destination.id)}
                >
                  <Trash size={14} />
                </Button>
              </Flex>
            ))}
          </Flex>
        </Box>
      )}
    </Box>
  );
}
